import { useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Modal } from "./Modal";
import { Alert, Button, ConfirmPrompt, Field, Input, Select, Spinner, cx } from "./ui";
import { Icon, type IconName } from "./icons";
import { ApiError } from "../lib/api";
import { absoluteTime, formatBytes } from "../lib/format";
import { useDatabases, useStartRestore } from "../lib/queries";
import type { Database, DatabaseRestore } from "../lib/types";

/**
 * Restoring a backup, from the backups tab.
 *
 * `RestoreModal` is the decision, kept short: the backup's moment and size in
 * the header, two equal choices — this database, or another one to look at the
 * backup first, which is often the better answer at a bad moment — one line
 * saying what the chosen one does, and the target's name typed, because the
 * API asks for it too. The button says what will happen ("Overwrite Shop",
 * "Restore to Shop copy").
 *
 * `RestoreActivity` follows it: a stepper whose steps say what is true —
 * nothing has changed while the current data is being saved — an elapsed
 * clock, one sentence about the step it is on, and on the far side the action
 * the outcome calls for: undo, try again, or read the log.
 *
 * A large database's restore takes hours, not minutes, so both say what they
 * can about that: the dialog that it will be long and that nobody needs to
 * wait for it, and the card how much of the backup taken first has been saved
 * — the only part the engine lets anyone measure. The restore itself reports
 * nothing until it ends, so it gets a sentence and a clock, not a bar.
 */

/**
 * A backup this size or larger is large: restoring it takes long enough that
 * the dialog says so, and the API emails the organization's admins when it
 * ends however quickly it went — which is what lets the dialog promise that.
 * The same figure as `LARGE_BACKUP_BYTES` in the API's `services/restores.ts`.
 */
export const LARGE_BACKUP_BYTES = 1024 ** 3;

/** A dump to restore: its full key, and what a person reads. */
export interface RestoreChoice {
  key: string;
  name: string;
  date?: string;
  size?: number;
}

/**
 * When a dump was taken, from its name — Dokploy names each one after the ISO
 * time it was written with `:` and `.` swapped for `-`. Null for a name in any
 * other shape, and the caller falls back to the name itself.
 */
export function backupTakenAt(name: string): string | null {
  const match = /(\d{4}-\d{2}-\d{2})T(\d{2})-(\d{2})-(\d{2})-(\d{3})Z/.exec(name);
  if (!match) return null;
  const iso = `${match[1]}T${match[2]}:${match[3]}:${match[4]}.${match[5]}Z`;
  return Number.isFinite(Date.parse(iso)) ? iso : null;
}

type Destination = "replace" | "elsewhere";

export function RestoreModal({
  orgId,
  database,
  choice,
  onClose,
  onStarted,
}: {
  orgId: string;
  database: Database;
  choice: RestoreChoice | null;
  onClose: () => void;
  onStarted: (restore: DatabaseRestore) => void;
}) {
  const { t } = useTranslation("database");
  const start = useStartRestore(orgId, database.id);
  const all = useDatabases(orgId, 100);
  const [destination, setDestination] = useState<Destination>("replace");
  const [otherId, setOtherId] = useState<string>("");
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);

  /*
   * Same engine, same region, running. The API is the authority — it also
   * requires the same server, which the dashboard cannot see — but offering a
   * database it would refuse on sight is worse than a short list.
   */
  const others = (all.data?.data ?? []).filter(
    (d) => d.id !== database.id && d.engine === database.engine && d.region === database.region && d.state === "running",
  );
  const other = others.find((d) => d.id === otherId) ?? others[0] ?? null;
  const replacing = destination === "replace";
  const target = replacing ? database : other;
  const confirmed = target !== null && (typed.trim() === target.name || typed.trim() === target.slug);
  const taken = choice ? (choice.date ?? backupTakenAt(choice.name)) : null;
  const large = (choice?.size ?? 0) >= LARGE_BACKUP_BYTES;
  // The moment being brought back, and how big it is: the header says it once.
  const moment = choice
    ? [
        taken ? t("restore.from", { when: absoluteTime(taken) }) : choice.name,
        // A size is one word: "38 GB" must not wrap between the two.
        choice.size !== undefined ? formatBytes(choice.size).replace(" ", "\u00a0") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : undefined;

  function reset() {
    setTyped("");
    setError(null);
    setDestination("replace");
    setOtherId("");
  }

  function close() {
    reset();
    onClose();
  }

  async function submit() {
    if (!choice || !target || !confirmed) return;
    setError(null);
    try {
      const restore = await start.mutateAsync({
        key: choice.key,
        ...(target.id !== database.id ? { targetDatabaseId: target.id } : {}),
        confirm: typed.trim(),
      });
      reset();
      onStarted(restore);
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : t("restore.failedToStart"));
    }
  }

  return (
    <Modal open={choice !== null} onClose={close} title={t("restore.title")} description={moment}>
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        {error && <Alert>{error}</Alert>}

        <fieldset>
          <legend className="micro mb-1.5">{t("restore.where")}</legend>
          <div role="radiogroup" className="grid grid-cols-2 gap-1.5">
            <Choice selected={replacing} tone="danger" onSelect={() => setDestination("replace")}>
              {t("restore.thisDatabase")}
            </Choice>
            <Choice selected={!replacing} onSelect={() => setDestination("elsewhere")}>
              {t("restore.anotherDatabase")}
            </Choice>
          </div>
          <p className="mt-2 text-[13px] leading-5 text-fg-muted">
            {replacing
              ? t("restore.replaceHint", { name: database.name })
              : t("restore.elsewhereHint", { name: database.name })}
          </p>
        </fieldset>

        {!replacing &&
          (others.length > 0 ? (
            <Select
              aria-label={t("restore.into")}
              value={other?.id ?? ""}
              onChange={(event) => setOtherId(event.target.value)}
            >
              {others.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </Select>
          ) : (
            <p className="text-[13px] leading-5 text-fg-subtle">
              {t("restore.noOthers")}{" "}
              <Link to="/databases/new" className="font-medium text-accent hover:underline">
                {t("restore.createOne")}
              </Link>
            </p>
          ))}

        {target && (
          <>
            <ul className="space-y-1 text-[13px] leading-5 text-fg-subtle">
              {large && (
                <li className="flex gap-2">
                  <Icon name="clock" className="mt-0.5 size-3.5 shrink-0" />
                  <span>{t("restore.largeNote", { size: formatBytes(choice?.size) })}</span>
                </li>
              )}
              <li className="flex gap-2">
                <Icon name="warning" className="mt-0.5 size-3.5 shrink-0" />
                <span>{t("restore.appsNote", { name: target.name })}</span>
              </li>
            </ul>

            <Field
              label={<ConfirmPrompt phrase={target.name} sentence={(slot) => t("restore.confirmLabel", { name: slot })} />}
              htmlFor="restore-confirm"
            >
              <Input
                id="restore-confirm"
                value={typed}
                autoComplete="off"
                spellCheck={false}
                onChange={(event) => setTyped(event.target.value)}
              />
            </Field>
          </>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-line pt-4 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={close}>
            {t("restore.cancel")}
          </Button>
          <Button
            type="submit"
            variant={replacing ? "danger" : "primary"}
            icon="refresh"
            disabled={!confirmed}
            loading={start.isPending}
          >
            {replacing
              ? t("restore.submitReplace", { name: database.name })
              : target
                ? t("restore.submitElsewhere", { name: target.name })
                : t("restore.action")}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** One of the two places a backup can go. Overwriting reads red once chosen: it replaces data. */
function Choice({
  selected,
  onSelect,
  tone,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  tone?: "danger";
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className={cx(
        "rounded-md border px-3 py-2 text-sm transition-colors duration-150 ease-smooth",
        "focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none",
        selected
          ? tone === "danger"
            ? "border-danger/60 bg-danger/5 font-medium text-fg"
            : "border-accent bg-accent/10 font-medium text-fg"
          : "border-line text-fg-muted hover:border-line-strong hover:text-fg",
      )}
    >
      {children}
    </button>
  );
}

const ACTIVE = new Set(["saving", "restoring"]);

export type StepState = "pending" | "running" | "done" | "failed" | "unknown";

/**
 * The two steps' states — backing up, then restoring — from what the API
 * recorded.
 *
 * Not positional. A restore that failed while its safety copy was still being
 * taken never reached the restore at all, and a track painting the restore
 * step as run above "nothing was restored" would be claiming the opposite of
 * the sentence under it — which is exactly what the first version did.
 * `restoreStartedAt` says whether the restore step was ever entered.
 */
export function restoreSteps(restore: Pick<DatabaseRestore, "status" | "restoreStartedAt">): [StepState, StepState] {
  const { status } = restore;
  if (status === "saving") return ["running", "pending"];
  if (status === "restoring") return ["done", "running"];
  if (status === "succeeded") return ["done", "done"];
  // failed or unknown: which step it stopped in decides which one says so.
  if (restore.restoreStartedAt === null) return [status, "pending"];
  return ["done", status];
}

/** Seconds as `m:ss`, or `h:mm:ss` past the hour — a clock, not a sentence. */
export function elapsedClock(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s % 60)}` : `${m}:${pad(s % 60)}`;
}

/** How long a finished restore took, short: `45s`, `14m`, `2h 14m`. */
export function tookShort(startIso: string, endIso: string | null): string | null {
  if (!endIso) return null;
  const seconds = Math.max(0, Math.round((Date.parse(endIso) - Date.parse(startIso)) / 1000));
  if (!Number.isFinite(seconds)) return null;
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const rest = minutes % 60;
  return rest === 0 ? `${Math.floor(minutes / 60)}h` : `${Math.floor(minutes / 60)}h ${rest}m`;
}

/** Ticks once a second while `on`, so an elapsed clock moves without the query polling faster. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [on]);
  return now;
}

/**
 * The restore in progress, or the last one's outcome, until dismissed or a day
 * old.
 *
 * One card that answers three things in order: what is happening (the title
 * names the step), how far it has got (a two-step track, the first measured
 * by what has reached storage, the second only a moving band — the engine
 * reports nothing until it ends), and what to do about it (one sentence, then
 * the action the outcome calls for).
 */
export function RestoreActivity({
  database,
  restores,
  canRestore,
  onRestore,
}: {
  database: Database;
  restores: DatabaseRestore[];
  canRestore: boolean;
  /** Opens the restore dialog on a dump — the undo, or a retry. */
  onRestore: (choice: RestoreChoice) => void;
}) {
  const { t } = useTranslation("database");
  const [showLog, setShowLog] = useState(false);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const current = restores.find((r) => ACTIVE.has(r.status)) ?? restores[0];
  const active = current ? ACTIVE.has(current.status) : false;
  const now = useNow(active);

  if (!current || current.id === dismissed) return null;
  // A finished restore is news for a day, then history.
  if (!active && current.finishedAt && Date.now() - Date.parse(current.finishedAt) > 86_400_000) return null;

  const intoThis = current.targetDatabaseId === database.id;
  const fromThis = current.sourceDatabaseId === database.id;
  const [backupStep, restoreStep] = restoreSteps(current);
  const taken = backupTakenAt(current.backupName);
  const status = current.status;
  const large = (current.backupBytes ?? 0) >= LARGE_BACKUP_BYTES;
  const lost = status === "restoring" && current.streamLostAt !== null;
  const name = intoThis ? database.name : t("restore.theTarget");

  const title = active
    ? !intoThis
      ? t("restore.titleElsewhere")
      : status === "saving"
        ? t("restore.titleSaving")
        : t("restore.titleRestoring")
    : t(`restore.result.${status as "succeeded" | "failed" | "unknown"}`);

  // The one sentence under the track: what is true now, or what happened.
  const line = active
    ? lost
      ? t("restore.lineLost", { name })
      : status === "saving"
        ? t("restore.lineSaving")
        : intoThis
          ? t("restore.lineRestoring", { name: database.name })
          : t("restore.lineElsewhere", { name: database.name })
    : status === "succeeded"
      ? t("restore.lineSucceeded", { when: taken ? absoluteTime(taken) : current.backupName })
      : status === "failed"
        ? (current.error ?? t("restore.lineFailed"))
        : t("restore.lineUnknown");

  // What the first step has saved so far, against the last backup's size when there was one.
  const saved = current.safetyBytes ?? 0;
  const expected = current.safetyExpectedBytes;
  const share = status === "saving" && saved > 0 && expected && expected > 0 ? Math.min(0.97, saved / expected) : null;
  const savedLabel =
    status === "saving" && saved > 0
      ? share !== null
        ? t("restore.savedOf", { done: formatBytes(saved), total: formatBytes(expected) })
        : t("restore.savedSoFar", { done: formatBytes(saved) })
      : null;

  const canUndo = canRestore && intoThis && !!current.safetyBackupKey && status !== "failed" && !active;
  const canRetry = canRestore && fromThis && status === "failed";

  return (
    <section
      aria-live="polite"
      className={cx(
        "animate-rise-in overflow-hidden rounded-lg border bg-surface",
        status === "failed" ? "border-danger/40" : status === "unknown" ? "border-warn/40" : "border-line",
      )}
    >
      <header className="flex items-start gap-3 px-4 pt-3.5">
        <StatusMark status={status} />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-3">
            <p className="truncate text-sm font-medium text-fg">{title}</p>
            <span
              className="shrink-0 font-mono text-xs tabular-nums text-fg-subtle"
              title={active ? t("restore.elapsed") : t("restore.took")}
            >
              {active ? elapsedClock((now - Date.parse(current.createdAt)) / 1000) : tookShort(current.createdAt, current.finishedAt)}
            </span>
          </div>
          {/* The moment gives way on a narrow screen; the size, which says how long this will be, does not. */}
          <p className="mt-0.5 flex min-w-0 gap-1 text-[13px] text-fg-subtle" title={current.backupName}>
            <span className="truncate">
              {taken ? t("restore.from", { when: absoluteTime(taken) }) : current.backupName}
            </span>
            {current.backupBytes !== null && (
              <span className="shrink-0 tabular-nums">· {formatBytes(current.backupBytes)}</span>
            )}
          </p>
        </div>
      </header>

      <div className="grid grid-cols-2 gap-2 px-4 pt-3 pb-3.5">
        <Segment
          step="saving"
          state={backupStep}
          label={t("restore.stepBackup")}
          share={share}
          detail={savedLabel}
          progressLabel={t("restore.stepBackup")}
        />
        <Segment step="restoring" state={restoreStep} label={t("restore.stepRestore")} share={null} />
      </div>

      <div className="space-y-2 border-t border-line bg-raised/30 px-4 py-3">
        <p className={cx("flex gap-2 text-[13px] leading-5", lost ? "text-fg" : "text-fg-muted")}>
          {lost && <Icon name="warning" className="mt-0.5 size-3.5 shrink-0 text-warn" />}
          <span className="min-w-0">{line}</span>
        </p>
        {active && large && !lost && (
          <p className="flex gap-2 text-[13px] leading-5 text-fg-subtle">
            <Icon name="clock" className="mt-0.5 size-3.5 shrink-0" />
            <span>{t("restore.lineLarge")}</span>
          </p>
        )}
        {canUndo && (
          <p className="flex gap-2 text-[13px] leading-5 text-fg-subtle">
            <Icon name="shield" className="mt-0.5 size-3.5 shrink-0 text-accent" />
            <span>{t("restore.undoHint")}</span>
          </p>
        )}

        {(!active || current.log.length > 0) && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            {canUndo && (
              <Button
                variant="secondary"
                size="sm"
                icon="refresh"
                onClick={() => {
                  const choice = undoChoice(current);
                  if (choice) onRestore(choice);
                }}
              >
                {t("restore.undo")}
              </Button>
            )}
            {canRetry && (
              <Button
                variant="secondary"
                size="sm"
                icon="refresh"
                onClick={() => onRestore(retryChoice(current))}
              >
                {t("restore.retry")}
              </Button>
            )}
            {current.log.length > 0 && (
              <Button variant="ghost" size="sm" onClick={() => setShowLog((v) => !v)} aria-expanded={showLog}>
                {showLog ? t("restore.hideLog") : t("restore.showLog")}
              </Button>
            )}
            {!active && (
              <Button variant="ghost" size="sm" className="ml-auto" onClick={() => setDismissed(current.id)}>
                {t("restore.dismiss")}
              </Button>
            )}
          </div>
        )}
      </div>

      {showLog && current.log.length > 0 && (
        <pre className="max-h-56 overflow-auto border-t border-line bg-inverse px-4 py-3 font-mono text-[11.5px] leading-relaxed whitespace-pre-wrap text-on-inverse/85">
          {current.log.join("\n")}
        </pre>
      )}
    </section>
  );
}

/**
 * The dialog's input for the card's two actions. With the size: the dialog
 * shows it, and a large backup's warning depends on it — a retry of a 40 GB
 * restore is as long as the first attempt.
 */
export function undoChoice(restore: DatabaseRestore): RestoreChoice | null {
  if (!restore.safetyBackupKey) return null;
  return {
    key: restore.safetyBackupKey,
    name: restore.safetyBackupKey.split("/").slice(-2).join("/"),
    ...(restore.safetyBytes !== null ? { size: restore.safetyBytes } : {}),
  };
}

export function retryChoice(restore: DatabaseRestore): RestoreChoice {
  return {
    key: restore.backupKey,
    name: restore.backupName,
    ...(restore.backupBytes !== null ? { size: restore.backupBytes } : {}),
  };
}

/** The card's status at a glance: a spinner while it runs, then what came of it. */
function StatusMark({ status }: { status: DatabaseRestore["status"] }) {
  if (status === "succeeded") return <Glyph tone="mint" icon="check" />;
  if (status === "failed") return <Glyph tone="danger" icon="close" />;
  if (status === "unknown") return <Glyph tone="warn" icon="warning" />;
  return (
    <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-accent/40 bg-accent-soft">
      <Spinner className="size-4 text-accent" />
    </span>
  );
}

const FILL: Record<StepState, string> = {
  pending: "w-0",
  running: "bg-accent",
  done: "w-full bg-mint",
  failed: "w-full bg-danger",
  unknown: "w-full bg-warn",
};

/**
 * One step of the track. A running step with a measure fills to it; one
 * without shows a moving band instead of a number nobody has.
 */
function Segment({
  step,
  state,
  label,
  share,
  detail,
  progressLabel,
}: {
  step: "saving" | "restoring";
  state: StepState;
  label: string;
  share: number | null;
  detail?: string | null;
  progressLabel?: string;
}) {
  const measured = state === "running" && share !== null;
  return (
    <div data-step={step} data-state={state} className="min-w-0">
      <div
        className={cx(
          "relative h-1.5 overflow-hidden rounded-full bg-line",
          state === "running" && !measured && "progress-sheen",
        )}
        {...(measured
          ? {
              role: "progressbar",
              "aria-valuemin": 0,
              "aria-valuemax": 100,
              "aria-valuenow": Math.floor(share * 100),
              "aria-label": progressLabel,
            }
          : {})}
      >
        {state !== "pending" && (state !== "running" || measured) && (
          <div
            className={cx(
              "absolute inset-y-0 left-0 rounded-full transition-[width] duration-700 ease-smooth",
              FILL[state],
            )}
            style={measured ? { width: `${share * 100}%` } : undefined}
          />
        )}
      </div>
      <p className="mt-1.5 flex min-w-0 items-center gap-1.5 text-xs">
        <StepMark state={state} />
        <span className={cx("truncate", state === "pending" ? "text-fg-subtle" : "text-fg")}>{label}</span>
      </p>
      {/* Its own line, under the label: half a phone's width holds one or the other, not both. */}
      {detail && <p className="mt-0.5 truncate pl-3 text-xs tabular-nums text-fg-subtle">{detail}</p>}
    </div>
  );
}

function StepMark({ state }: { state: StepState }) {
  if (state === "done") return <Icon name="check" className="size-3 shrink-0 text-mint" />;
  if (state === "failed") return <Icon name="close" className="size-3 shrink-0 text-danger" />;
  if (state === "unknown") return <Icon name="warning" className="size-3 shrink-0 text-warn" />;
  if (state === "running") return <span aria-hidden className="dot-status dot-status-pulse text-accent" />;
  return <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-line-strong" />;
}

function Glyph({ tone, icon }: { tone: "mint" | "danger" | "warn"; icon: IconName }) {
  const styles = {
    mint: "border-mint/40 bg-mint/10 text-mint",
    danger: "border-danger/40 bg-danger/10 text-danger",
    warn: "border-warn/40 bg-warn/10 text-warn",
  }[tone];
  return (
    <span className={cx("flex size-8 shrink-0 items-center justify-center rounded-full border", styles)}>
      <Icon name={icon} className="size-4" />
    </span>
  );
}
